import React, { useState } from "react";
import { Button, DatePicker, Input, Row, Col } from "@mtm/components";
import "antd/dist/antd.css";
import { isEmpty } from "../../helpers/ValidationHelpers";
import { ReCaptcha } from "../ReCaptcha/ReCaptcha";
import moment from "moment";
import {
  IPersonIDPData,
  InsertParameter,
  RegistrationComponentLoad,
  RegistrationEnums,
  RegistrationStatusDefaultValues,
  TrackAnalytics,
} from "../../types/ApplicationTypes";
import {
  getPersonAttributeKey,
  GetPersonRegistrationMatch,
  UserRegistration,
} from "../../services/RegistrationService";
import Styles from "./PersonMatch.module.scss";
import CommonBgStyles from "../../BackgroundCircles.module.scss";
import { DefaultAccommodations } from "../../types/ProfileTypes";
import { notification } from "antd";
import MTMAccountExistsModal from "../../helpers/MTMAccountExistsModal";
import { backArrowIcon } from "../../assets/images";

type Props = {
  pageType: string;
  setPageType: Function;
  personData: IPersonIDPData;
  setPersonId: Function;
  setWandaEmailId: Function;
  wandaOrgId: number;
};

const FieldNames = {
  zipCode: "zipCode",
  dateOfBirth: "dateOfBirth",
};
type DynamicObject = {
  [key: string]: any;
};

const PersonMatch: React.FC<Props> = ({
  pageType,
  setPageType,
  personData,
  setPersonId,
  setWandaEmailId,
  wandaOrgId,
}) => {
  const [personInfo, setPersonInfo] = useState<DynamicObject>(
    initializeFieldValue()
  );

  const [isAccountFound, setIsAccountFound] = useState<boolean>(false);
  const [btnClicked, setBtnClicked] = useState<boolean>(false);

  const { zipCode, dateOfBirth } = personInfo;
  function initializeFieldValue() {
    return {
      [FieldNames.zipCode]: "",
      [FieldNames.dateOfBirth]: "",
    };
  }
  function isFieldsValid() {
    return Object.keys(personInfo).every((key) => !isEmpty(personInfo[key]));
  }

  function handlePersonMatchChange(value: any, field: string) {
    if (field === "zipCode") {
      setPersonInfo((personInfo) => ({
        ...personInfo,
        [field]: value.replace(/[^0-9]/gi, ""),
      }));
    }
    if (field === "dateOfBirth") {
      setPersonInfo((personInfo) => ({
        ...personInfo,
        [field]: value ? moment(value).format("YYYY-MM-DD") : null,
      }));
    }
  }

  async function handlePersonMatch(execute: () => Promise<string>) {
    setBtnClicked(true);
    const recaptchaToken = await execute();
    const data = {
      firstName: personData.firstName,
      lastName: personData.lastName,
      phoneNumber: personData.phoneNumber,
      email: personData.emailAddress,
      zipCode: personInfo.zipCode,
      dateOfBirth: personInfo.dateOfBirth,
      searchPattern: RegistrationEnums.PersonMatch,
      recaptchaToken: recaptchaToken,
    };
    var result = await GetPersonRegistrationMatch(data); //Check for Person match

    if (result.success === true) {
      TrackAnalytics(
        "/Registration",
        "Person Match found - Zipcode and DOB Inputted",
        "Person Match found"
      );
      setPersonId(result.personId);
      var registration: boolean = false;

      const token = await execute();
      const personData = {
        personId: result.personId,
        recaptchaToken: token,
      };
      const personAttributesList = await getPersonAttributeKey(personData);

      const personAttributes = personAttributesList.filter(
        (x: { keyTypeId: number }) =>
          x.keyTypeId === RegistrationEnums.WandaEmail
      );

      if (personAttributes.length > 0) {
        setWandaEmailId(personAttributes[0].personAttributesKey);
        setPageType(RegistrationComponentLoad.SingleWandaEmailValidation);
      } else {
        setIsAccountFound(true);
      }
    } else {
      TrackAnalytics(
        "/Registration",
        "Person Match not found - Zipcode and DOB Inputted",
        "Person Match not found"
      );
      registration = true;
    }

    if (registration) {
      const token = await execute();

      const dataInput = {
        firstName: personData.firstName,
        lastName: personData.lastName,
        zipCode: personInfo.zipCode,
        dateOfBirth: personInfo.dateOfBirth,
        recaptchaToken: token,
        personId: RegistrationStatusDefaultValues.DefaultPersonId,
        clientId: RegistrationEnums.clientId,
        sourceSystemID: RegistrationEnums.SourceSystemID,
        createRelation: true,
        organizationId: wandaOrgId,
        keyTypeId: RegistrationEnums.KeyTypeId,
        isActive: true,
        personTypeId: RegistrationEnums.PersonTypeId,
        gender: "N/A",
        detailsExist: true,
        personFound: false,
        roles: [
          {
            roleName: RegistrationEnums.Role,
          },
        ],
        personAttributes: {
          personInfo: {
            primaryLanguage: "English",
          },
          homeAddress: {
            address1: "123 Any Street",
            address2: "",
            city: "Columbus",
            state: "OH",
            zip: "43068",
            county: "",
          },
          mailingAddress: {
            address1: "123 Any Street",
            address2: "",
            city: "Columbus",
            state: "OH",
            zip: "43068",
            county: "",
          },
          personContact: {
            WandaEmail: personData.emailAddress,
            WandaEmailAddressAttribute: {
              notificationOn: true,
              communicationTypes: {
                informative: true,
                promotional: personData.promotionalCheck,
              },
            },
            primaryPhone: personData.phoneNumber,
            PrimaryPhoneAttribute: {
              canReceiveTexts: true,
              notificationOn: true,
              communicationTypes: {
                informative: true,
              },
            },
            primaryEmailAddress: personData.emailAddress,
            primaryEmailAddressAttribute:{
              notificationOn: true,
              communicationTypes: {
                informative: true,
                promotional: personData.promotionalCheck,
              },
            }
          },
        },
        wandaAccommodations: DefaultAccommodations(),
      };

      try {
        const output = await UserRegistration(dataInput);
        if (output.passwordLink === true) {
          setPageType(RegistrationComponentLoad.Message);
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description:
            "Password link generation failed. Please try again later.",
        });
      }
    }
  }

  const handleWandaLogin = () => {
    setIsAccountFound(false);
    window.location.hash = `/`;
    window.location.reload();
  };

  const handleAccountFoundCancel = () => {
    setIsAccountFound(false);
  };

  const handleBack = () => setPageType(RegistrationComponentLoad.IDPNotExists);

  return (
    <>
     <Row gutter={[0, 21]}>
        <Col xs={2} lg={4} />
        <Col>
          <Button
            link={true}
            clickHandler={handleBack}
            className={Styles.backBtn}
          >
            <div>
              <img src={backArrowIcon} alt="navigate-back" />
              Back
            </div>
          </Button>
        </Col>
        <Col xs={0} lg={24} />
      </Row>
      <div className={CommonBgStyles.bg_circle} />
      <div className={Styles.position}>
        <div className={"subTextFont " + Styles.personMatchDiv}>
          <p> Please provide some more information about yourself.</p>
          <div className={Styles.personMatchText}>
            <Input
              type="text"
              value={zipCode}
              placeholder="Zip Code"
              onChange={(e: any) => {
                handlePersonMatchChange(e.target.value, FieldNames.zipCode);
              }}
              maxLength={5}
              className="mtm-input-alternate"
            />
          </div>
          <DatePicker
            name="Date of birth"
            placeholder="Date of Birth (MM/DD/YYYY)"
            format={"MM/DD/YYYY"}
            value={
              dateOfBirth
                ? moment(moment(dateOfBirth).format("L"), "MM/DD/YYYY")
                : null
            }
            onChange={(e: any) => {
              handlePersonMatchChange(e, FieldNames.dateOfBirth);
            }}
            suffixIcon={<div></div>}
          ></DatePicker>
          <ReCaptcha>
            {(captcha) => (
              <Button
                clickHandler={(e) => handlePersonMatch(captcha.execute)}
                disabled={
                  !isFieldsValid() ||
                  btnClicked ||
                  personInfo.zipCode.length !== 5 ||
                  !captcha.isReady
                }
                className={
                  !isFieldsValid() ||
                  personInfo.zipCode.length !== 5 ||
                  !captcha.isReady
                    ? "wandaBtnDisabled"
                    : "wandaBtnEnabled"
                }
              >
                Continue
              </Button>
            )}
          </ReCaptcha>
        </div>
        <div>
          <MTMAccountExistsModal
            isAccountFound={isAccountFound}
            handleWandaLogin={handleWandaLogin}
            handleAccountFoundCancel={handleAccountFoundCancel}
          />
        </div>
      </div>
    </>
  );
};
export default PersonMatch;
